import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import { ClosingStatusEnumType, DealStatusEnumType } from '__generated__/graphql';
import dayjs from 'dayjs';
import { FC, useState } from 'react';

import { FormInput, FormPanelWithReadMode } from 'components/FormPanel';
import EditorFormInput from 'components/QuillEditor/EditorFormInput';

import {
  POST_AUDIENCE_OPTIONS,
  PostAudience,
  audienceFromPost,
  audienceToVariables,
  isAudienceMissing,
  isStageSelectionMissing,
} from './post-audience';

const UPDATE_POST_MUTATION = gql(`
  mutation UpdatePostOnDeal($id: ID!, $title: String!, $message: String!, $dealId: ID, $targetClosingStatuses: [ClosingStatusEnumType!], $targetInvitees: Boolean, $targetSyndicateUsers: Boolean) {
    updatePost(id: $id, title: $title, message: $message, dealId: $dealId, targetClosingStatuses: $targetClosingStatuses, targetInvitees: $targetInvitees, targetSyndicateUsers: $targetSyndicateUsers) {
      id
      status
      title
      message
      deal {
        id
        companyInformation {
          id
          company {
            name
          }
        }
      }
      createdAt
      targetClosingStatuses
      targetInvitees
      targetSyndicateUsers
      targetRecipientCount
    }
  }
`);

const UpdatePost: FC<{
  id: string;
  title: string;
  message: string;
  createdAt: string;
  deal?:
    | {
        id: string;
        status: DealStatusEnumType;
      }
    | null
    | undefined;
  targetClosingStatuses?: ClosingStatusEnumType[] | null | undefined;
  targetInvitees?: boolean;
  targetSyndicateUsers?: boolean;
  isBeingEdited?: (boolean) => void;
}> = ({
  id,
  title,
  message,
  createdAt,
  deal,
  targetClosingStatuses,
  targetInvitees,
  targetSyndicateUsers,
  isBeingEdited,
}) => {
  const storedAudience = audienceFromPost({
    targetClosingStatuses,
    targetInvitees,
    targetSyndicateUsers,
  });
  const [updatePost, { loading, error }] = useMutation(UPDATE_POST_MUTATION, {
    refetchQueries: ['getPost'],
  });
  const [targetAudienceWarning, setTargetAudienceWarning] = useState(
    storedAudience.includes('SYNDICATE_USERS')
  );
  const [targetAudienceError, showTargetAudienceError] = useState(false);
  const targetAudienceOptionsToShow = POST_AUDIENCE_OPTIONS.filter(
    option => deal || option.value === 'SYNDICATE_USERS'
  );

  return (
    <>
      <FormPanelWithReadMode
        loading={loading}
        error={error}
        isInReadOnlyMode={readOnlyMode => isBeingEdited && isBeingEdited(!readOnlyMode)}
        onSubmit={data => {
          showTargetAudienceError(false);
          if (
            isAudienceMissing(data.targetAudience) ||
            isStageSelectionMissing(data.targetAudience, data.targetClosingStatuses)
          ) {
            showTargetAudienceError(true);
            return;
          }
          updatePost({
            variables: {
              id,
              title: data.title,
              message: data.content,
              dealId: deal ? deal.id : undefined,
              ...audienceToVariables(data.targetAudience, data.targetClosingStatuses),
            },
          });
        }}
        submitButtonLabel="Save"
      >
        {dayjs(createdAt)
          .startOf('day')
          .isAfter(dayjs('2024-09-11T00:00:00+05:30').startOf('day')) ? (
          <>
            <FormInput
              fullWidth
              type="checkbox-group"
              fieldName="targetAudience"
              label="Who should see this post? (select all that apply)"
              defaultValue={storedAudience}
              options={targetAudienceOptionsToShow}
              onChange={value => {
                const audience: PostAudience[] = value.targetAudience || [];
                setTargetAudienceWarning(audience.includes('SYNDICATE_USERS'));
              }}
              validators={{
                required: true,
              }}
            />
            <FormInput
              fullWidth
              type="checkbox-group"
              fieldName="targetClosingStatuses"
              label="Choose investors by stage of investment"
              defaultValue={
                targetClosingStatuses ||
                (deal?.status === DealStatusEnumType.Exited ||
                deal?.status === DealStatusEnumType.PartiallyExited ||
                deal?.status === DealStatusEnumType.Wired
                  ? [ClosingStatusEnumType.Active]
                  : [ClosingStatusEnumType.Committed, ClosingStatusEnumType.Transferred])
              }
              compact
              options={
                deal?.status === DealStatusEnumType.Exited ||
                deal?.status === DealStatusEnumType.PartiallyExited ||
                deal?.status === DealStatusEnumType.Wired
                  ? [{ label: 'Active', value: ClosingStatusEnumType.Active }]
                  : [
                      { label: 'Committed', value: ClosingStatusEnumType.Committed },
                      { label: 'Transferred', value: ClosingStatusEnumType.Transferred },
                    ]
              }
              conditionsToShow={{
                matches: [
                  {
                    field: 'targetAudience',
                    condition: 'includes',
                    value: 'CLOSINGS',
                  },
                ],
              }}
              validators={{
                required: true,
              }}
            />
          </>
        ) : (
          <></>
        )}
        <FormInput
          fullWidth
          type="text"
          fieldName="title"
          label="Title"
          defaultValue={title}
          validators={{
            required: true,
          }}
        />
        <FormInput
          fullWidth
          type="custom"
          fieldName="content"
          label="Content"
          defaultValue={message}
          customInput={EditorFormInput}
          validators={{
            required: true,
          }}
        />
      </FormPanelWithReadMode>
      {targetAudienceError ? (
        <p className="text-red-500">
          Please select at least one audience, and at least one stage of investment if you are
          targeting the investors in this deal
        </p>
      ) : null}
      {!deal || targetAudienceWarning ? (
        <p className="col-span-full text-red-500 text-sm mb-8">
          This post will go out to all investors in the syndicate.
        </p>
      ) : null}
    </>
  );
};

export default UpdatePost;
